import { CircleHelp } from 'lucide-react';
import type { ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * A control with a "?" beside it that explains it, on hover or keyboard focus.
 *
 * The explanation sits on its own small icon rather than on the control: a
 * tooltip that pops up whenever the pointer crosses the button is noise for
 * everyone who just wants to click it, while a "?" is there for whoever wants
 * the reason and ignorable otherwise. It also works on a disabled control,
 * which receives no pointer events and so could never open a tooltip itself,
 * and the icon is a real button, so a keyboard user can reach it too.
 *
 * Renders the control alone when there is nothing to explain.
 */
export function HintTooltip({
  hint,
  contentClassName,
  children,
}: {
  hint: string | undefined;
  /**
   * For callers on a raised layer. The tooltip portals to `body` at `z-50`,
   * which a full-screen overlay at `z-[100]` would paint over.
   */
  contentClassName?: string;
  children: ReactNode;
}) {
  if (!hint) return <>{children}</>;
  return (
    <span className="inline-flex shrink-0 items-center gap-1">
      {children}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="More info"
            className="inline-flex text-slate-400 transition-colors hover:text-slate-600"
          >
            <CircleHelp className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className={contentClassName}>
          {hint}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}
